import { trapFocus, removeTrapFocus } from '@theme/focus';

// Country/language availability is rendered by Shopify, never inferred from a flag.
const aliases = {
  US: 'United States USA 美国', CA: 'Canada 加拿大', MX: 'Mexico México 墨西哥',
  GB: 'United Kingdom UK Britain 英国', FR: 'France 法国', DE: 'Germany Deutschland 德国',
  IT: 'Italy Italia 意大利', ES: 'Spain España 西班牙', AU: 'Australia 澳大利亚', NZ: 'New Zealand 新西兰', SG: 'Singapore 新加坡',
  AT: 'Austria Österreich 奥地利', BE: 'Belgium België Belgique 比利时', BG: 'Bulgaria България 保加利亚',
  HR: 'Croatia Hrvatska 克罗地亚', CY: 'Cyprus Κύπρος 塞浦路斯', CZ: 'Czechia Czech Republic Česko 捷克',
  DK: 'Denmark Danmark 丹麦', EE: 'Estonia Eesti 爱沙尼亚', FI: 'Finland Suomi 芬兰', GR: 'Greece Ελλάδα 希腊',
  HU: 'Hungary Magyarország 匈牙利', IE: 'Ireland Éire 爱尔兰', LV: 'Latvia Latvija 拉脱维亚', LT: 'Lithuania Lietuva 立陶宛',
  LU: 'Luxembourg Lëtzebuerg 卢森堡', MT: 'Malta 马耳他', NL: 'Netherlands Nederland Holland 荷兰', PL: 'Poland Polska 波兰',
  PT: 'Portugal 葡萄牙', RO: 'Romania România 罗马尼亚', SK: 'Slovakia Slovensko 斯洛伐克', SI: 'Slovenia Slovenija 斯洛文尼亚', SE: 'Sweden Sverige 瑞典',
};
const preferred = {
  MX: 'es', ES: 'es', FR: 'fr', DE: 'de', IT: 'it', AT: 'de', BE: 'nl', BG: 'bg', CY: 'el', CZ: 'cs',
  DK: 'da', FI: 'fi', GR: 'el', HR: 'hr', HU: 'hu', IE: 'en', LT: 'lt', LU: 'fr', NL: 'nl', NO: 'nb',
  PL: 'pl', PT: 'pt-PT', RO: 'ro', SK: 'sk', SE: 'sv', CH: 'de',
};
// Keep EU Market membership separate from the countries shown inside Other EU countries.
const featuredEuropeCodes = new Set(['NL', 'RO', 'FI', 'IE', 'SE', 'GR']);
const isOtherEU = (country) => Boolean(country?.eu) && !featuredEuropeCodes.has(country.code);
const escapeHTML = (value) => String(value).replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]);
const normalize = (value) => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
const interpolate = (text, values) => text.replace(/\{(\w+)\}/g, (_, key) => values[key] ?? '');
const languageFor = (country) => country.languages.find((l) => l.code === preferred[country.code])
  ?? country.languages.find((l) => country.code === 'PT' && l.code === 'pt')
  ?? country.languages.find((l) => l.code === 'en') ?? country.languages[0];
const initialized = new WeakMap();

function initialize(root) {
  const existingTrigger = root.querySelector('[data-loc-open]');
  if (existingTrigger && initialized.get(root) === existingTrigger) {
    // Section hydration may restore the server-rendered hidden attribute.
    if (existingTrigger.hidden) existingTrigger.hidden = false;
    return;
  }
  const payload = root.querySelector('[data-loc-data]');
  if (!payload) return;
  const data = JSON.parse(payload.textContent);
  const countries = data.countries;
  if (data.currentLanguage === 'nb' && typeof Intl.DisplayNames === 'function') {
    const countryNames = new Intl.DisplayNames(['nb'], { type: 'region' });
    const languageNames = new Intl.DisplayNames(['nb'], { type: 'language' });
    countries.forEach((country) => {
      country.name = countryNames.of(country.code) || country.name;
      country.languages.forEach((language) => {
        language.subtitle = languageNames.of(language.code) || language.subtitle;
      });
    });
  }
  if (!countries.length) return;
  const ui = data.strings;
  const find = (selector) => root.querySelector(selector);
  const dialog = find('[data-loc-dialog]');
  const form = dialog.querySelector('form');
  const trigger = find('[data-loc-open]');
  const search = find('[data-loc-search]');
  const results = find('[data-loc-results]');
  const languageList = find('[data-loc-languages]');
  const summary = find('[data-loc-selection]');
  const notice = find('[data-loc-notice]');
  const apply = find('[data-loc-apply]');
  const current = { country: data.currentCountry, language: data.currentLanguage };
  let draft = { ...current };
  let region = countries.find((c) => c.code === current.country)?.region ?? 'Europe';
  let euOpen = false;
  let ownerDrawer;
  let opener;
  let busy = false;
  const byCode = (code) => countries.find((c) => c.code === code);
  const regionNames = { Europe: ui.europe, 'North America': ui.northAmerica, 'Asia Pacific': ui.asiaPacific };
  const euCount = countries.filter(isOtherEU).length;
  initialized.set(root, trigger);
  root.dataset.locReady = 'true';
  trigger.hidden = false;
  find('[data-loc-count]').textContent = `${countries.length} ${ui.destinations}`;
  root.querySelectorAll('[data-loc-region]').forEach((button) => {
    button.hidden = !countries.some((c) => c.region === button.dataset.locRegion);
  });

  function setStep(value) {
    dialog.dataset.step = value;
    root.querySelectorAll('[data-loc-step]').forEach((b) => b.setAttribute('aria-selected', String(b.dataset.locStep === value)));
  }
  function message(value = '') {
    notice.textContent = value;
    notice.hidden = !value;
  }
  function selection() {
    const c = byCode(draft.country);
    const l = c?.languages.find((item) => item.code === draft.language);
    summary.textContent = c ? `${c.name} · ${c.currency}${l ? ` · ${l.name}` : ''}` : '';
    apply.disabled = !c || !l || busy;
  }
  function renderLanguages() {
    const c = byCode(draft.country);
    const recommendation = languageFor(c);
    const languages = recommendation ? [recommendation, ...c.languages.filter((l) => l.code !== recommendation.code)] : c.languages;
    find('[data-loc-available]').textContent = interpolate(ui.available, { country: c.name });
    languageList.innerHTML = languages.map((l) => `<label class="ta-loc-language"><input type="radio" name="ta-loc-language-${escapeHTML(dialog.id)}" value="${escapeHTML(l.code)}" ${draft.language === l.code ? 'checked' : ''}><span lang="${escapeHTML(l.code)}">${escapeHTML(l.name)}<small>${escapeHTML(l.subtitle)}</small></span>${l.code === recommendation?.code ? `<em>${escapeHTML(ui.recommended)}</em>` : ''}<i aria-hidden="true"></i></label>`).join('');
    find('.ta-loc-language-panel').scrollTop = 0;
    if (!c.languages.length) message(ui.error);
    selection();
  }
  function countryButton(c) {
    return `<button type="button" class="ta-loc-country" data-loc-country="${escapeHTML(c.code)}" aria-pressed="${c.code === draft.country}" ${!c.languages.length ? 'disabled' : ''}><img src="${escapeHTML(c.flag)}" width="24" height="18" alt="" loading="lazy"><span>${escapeHTML(c.name)}<small>${escapeHTML(c.currency)}</small></span><i aria-hidden="true">✓</i></button>`;
  }
  function renderCountries() {
    const query = normalize(search.value);
    find('[data-loc-clear]').hidden = !query;
    find('.ta-loc-regions').hidden = Boolean(query);
    root.querySelectorAll('[data-loc-region]').forEach((b) => b.setAttribute('aria-selected', String(b.dataset.locRegion === region)));
    let list;
    let heading;
    let prefix = '';
    let suffix = '';
    if (query) {
      list = countries.filter((c) => normalize(`${c.name} ${aliases[c.code] ?? ''} ${c.code}`).includes(query));
      heading = `${ui.country} · ${list.length}`;
    } else if (region === 'Europe' && euOpen) {
      list = countries.filter(isOtherEU);
      heading = `${ui.eu} · ${list.length}`;
      prefix = `<button type="button" class="ta-loc-eu-back" data-loc-eu-back>‹ ${escapeHTML(ui.allEurope)}</button>`;
    } else {
      list = countries.filter((c) => c.region === region && !isOtherEU(c));
      heading = regionNames[region];
      if (region === 'Europe' && euCount) suffix = `<button type="button" class="ta-loc-eu-entry" data-loc-eu-open><span class="ta-loc-globe" aria-hidden="true">◎</span><span>${escapeHTML(ui.eu)}<small>${euCount} ${escapeHTML(ui.destinations)}</small></span><i aria-hidden="true">›</i></button>`;
    }
    results.innerHTML = `${prefix}<div class="ta-loc-list-heading">${escapeHTML(heading)}</div><div class="ta-loc-country-scroll">${list.length ? `<div class="ta-loc-countries">${list.map(countryButton).join('')}</div>` : query ? `<div class="ta-loc-empty">${escapeHTML(ui.empty)}<button type="button" data-loc-clear>${escapeHTML(ui.clear)}</button></div>` : ''}</div>${suffix}`;
  }
  function focusSelection() {
    const selected = results.querySelector('[data-loc-country][aria-pressed=true]');
    selected?.scrollIntoView({ block: 'nearest' });
  }
  function open() {
    if (busy || dialog.open) return;
    // Release Horizon's parent drawer trap before opening a native top-layer modal.
    ownerDrawer = root.closest('.menu-drawer-container[open]');
    opener = trigger;
    removeTrapFocus();
    draft = { ...current };
    region = byCode(draft.country)?.region ?? 'Europe';
    euOpen = isOtherEU(byCode(draft.country));
    search.value = '';
    message();
    setStep('country');
    renderCountries();
    renderLanguages();
    dialog.showModal();
    trigger.setAttribute('aria-expanded', 'true');
    dialog.querySelector('[data-loc-close]').focus();
    requestAnimationFrame(focusSelection);
  }
  function close() {
    if (!busy) dialog.close();
  }
  trigger.addEventListener('click', open);
  dialog.addEventListener('close', () => {
    trigger.setAttribute('aria-expanded', 'false');
    if (ownerDrawer?.isConnected && ownerDrawer.open) trapFocus(ownerDrawer);
    (opener?.isConnected ? opener : trigger).focus({ preventScroll: true });
  });
  dialog.addEventListener('cancel', (event) => { event.preventDefault(); close(); });
  dialog.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target === dialog) {
      const rect = dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) close();
    }
    if (target.closest('[data-loc-close]')) close();
    const countryButton = target.closest('[data-loc-country]');
    if (countryButton && !countryButton.disabled) {
      const c = byCode(countryButton.dataset.locCountry);
      const oldLanguage = byCode(draft.country)?.languages.find((l) => l.code === draft.language);
      const oldLanguageAvailable = c.languages.some((l) => l.code === draft.language);
      draft.country = c.code;
      draft.language = languageFor(c)?.code ?? '';
      message();
      if (!oldLanguageAvailable) {
        message(interpolate(ui.fallback, { language: oldLanguage?.name ?? current.language, fallback: languageFor(c)?.name ?? '' }));
      }
      results.querySelectorAll('[data-loc-country]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.locCountry === draft.country)));
      renderLanguages();
      if (matchMedia('(max-width: 749px)').matches) {
        setStep('language');
        find('[data-loc-step=language]').focus();
      }
    }
    if (target.closest('[data-loc-eu-open]')) { euOpen = true; renderCountries(); find('[data-loc-eu-back]').focus(); }
    if (target.closest('[data-loc-eu-back]')) { euOpen = false; renderCountries(); find('[data-loc-eu-open]')?.focus(); }
    const regionButton = target.closest('[data-loc-region]');
    if (regionButton) { region = regionButton.dataset.locRegion; euOpen = false; renderCountries(); }
    const stepButton = target.closest('[data-loc-step]');
    if (stepButton) setStep(stepButton.dataset.locStep);
    if (target.closest('[data-loc-clear]')) { search.value = ''; renderCountries(); search.focus(); }
  });
  search.addEventListener('input', renderCountries);
  languageList.addEventListener('change', (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;
    draft.language = event.target.value;
    message();
    selection();
  });
  dialog.addEventListener('keydown', (event) => {
    if (event.key === 'Tab') {
      const visible = [...dialog.querySelectorAll('button:enabled,input:not([type=hidden]):enabled')].filter((el) => el.getClientRects().length);
      const first = visible[0];
      const last = visible.at(-1);
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    }
    const tab = event.target instanceof Element && event.target.closest('[role=tab]');
    if (tab && ['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
      event.preventDefault();
      const tabs = [...tab.parentElement.querySelectorAll('[role=tab]')].filter((el) => !el.hidden);
      const index = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (tabs.indexOf(tab) + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
      tabs[index].click(); tabs[index].focus();
    }
  });
  form.addEventListener('submit', (event) => {
    const c = byCode(draft.country);
    if (busy || !c?.languages.some((l) => l.code === draft.language)) {
      event.preventDefault(); message(ui.error); return;
    }
    if (draft.country === current.country && draft.language === current.language) {
      event.preventDefault(); close(); return;
    }
    form.elements.country_code.value = draft.country;
    form.elements.language_code.value = draft.language;
    // Shared EU pages stay valid when switching between the 23 EU countries.
    // Moving between separate Markets still returns to their localized homepage.
    if (draft.country !== current.country && !(c.eu && byCode(current.country)?.eu)) form.elements.return_to.value = '/';
    busy = true;
    apply.disabled = true;
    apply.textContent = ui.loading;
  });
  // Native form POST owns redirects/errors. Browser history must restore interactivity.
  window.addEventListener('pageshow', () => { busy = false; apply.textContent = ui.apply; selection(); });
}
function initializeAll() { document.querySelectorAll('[data-loc-widget]').forEach(initialize); }
initializeAll();
document.addEventListener('shopify:section:load', initializeAll);
// Horizon can replace the mobile drawer DOM after the initial module has run.
new MutationObserver((records) => {
  const candidates = new Set();
  records.forEach((record) => {
    if (record.type === 'attributes') {
      if (!record.target.matches('[data-loc-open]')) return;
      const owner = record.target.closest('[data-loc-widget]');
      if (owner) candidates.add(owner);
      return;
    }
    record.addedNodes.forEach((node) => {
      if (!(node instanceof Element)) return;
      const owner = node.closest('[data-loc-widget]');
      if (owner) candidates.add(owner);
      node.querySelectorAll('[data-loc-widget]').forEach((root) => candidates.add(root));
    });
  });
  candidates.forEach((root) => { if (root.isConnected) initialize(root); });
}).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden'] });
